import type { SCAPIShopperProductsSchemas } from '@/types';
import type { PageWithComponentData } from '@/lib/util/pageLoader.server';
import type { NavigationPillar } from '@/lib/navigation-pillars';

export const ANALYTICS_CONTENT_GROUP_ATTRIBUTE_ID = 'analyticsContentGroup';

/** GA4 `content_group` values from the Eataly analytics page taxonomy. */
export const CONTENT_GROUP = {
    PRODUCT_LIST: 'product list',
    PRODUCT_DETAILS: 'product details',
    CLASS_LIST: 'class list',
    CLASS_DETAILS: 'class details',
    EVENT_LIST: 'event list',
    EVENT_DETAILS: 'event details',
    RESTAURANT_LIST: 'restaurant list',
    RESTAURANT_DETAILS: 'restaurant details',
    STORE_LIST: 'store list',
    STORE_DETAILS: 'store details',
    BLOG: 'blog',
    RECIPES: 'recipes',
    MAKERS: 'makers',
    SEARCH: 'search',
    CART: 'cart',
    CHECKOUT: 'checkout',
    LOGIN_SIGNUP: 'login signup',
    ACCOUNT: 'account',
    ORDER_CONFIRMATION: 'order confirmation',
    HOME_SPLASH: 'home page - splash',
    HOME_ONLINE_SHOP: 'home page - online shop',
    HOME_EVENTS: 'home page - events',
    HOME_CLASSES: 'home page - classes',
    HOME_RESTAURANTS: 'home page - restaurants',
    HOME_BUSINESS: 'home page - business',
    NEWS_LIST: 'news list',
    NEWS_DETAILS: 'news details',
    JOB_LIST: 'job list',
    JOB_DETAILS: 'job details',
    SERVICES: 'services',
    BUSINESS: 'business',
    GIFT_HUB: 'gift hub',
    SEASONAL: 'seasonal',
    ABOUT_US: 'about us',
    WORK_WITH_US: 'work with us',
    HELP: 'help',
    NEWSLETTER: 'newsletter',
    CLUB: 'club',
    SERVICE: 'service',
} as const;

export type ContentGroupValue = (typeof CONTENT_GROUP)[keyof typeof CONTENT_GROUP];

/** All GA4 content_group values, used as Page Designer enum options. */
export const CONTENT_GROUP_VALUES = Object.values(CONTENT_GROUP) as ContentGroupValue[];

const CONTENT_GROUP_VALUE_SET = new Set<string>(CONTENT_GROUP_VALUES);

export function isContentGroupValue(value: string): value is ContentGroupValue {
    return CONTENT_GROUP_VALUE_SET.has(value);
}

/** Reads and validates analyticsContentGroup from Page Designer page attributes. */
export function readPageDesignerContentGroup(
    page: Pick<PageWithComponentData, 'data'> | undefined
): ContentGroupValue | undefined {
    const pageData = page?.data as Record<string, unknown> | undefined;
    const raw = pageData?.[ANALYTICS_CONTENT_GROUP_ATTRIBUTE_ID];
    if (typeof raw !== 'string' || !raw.trim()) {
        return undefined;
    }

    const normalized = raw.trim();
    return isContentGroupValue(normalized) ? normalized : undefined;
}

export type PlpRouteKind = 'plp' | 'pillarPage' | 'fullEditorialCategoryPage';

export type EditorialRouteKind =
    | 'folderListing'
    | 'newsListing'
    | 'locationPage'
    | 'pageDesigner'
    | 'contentPillarPage';

export type CategoryBoundPillarPageKind = 'pillarPage' | 'fullEditorialCategoryPage';

export type ResolveEditorialRouteContentGroupArgs = {
    page?: Pick<PageWithComponentData, 'data' | 'id' | 'typeId'>;
    kind: EditorialRouteKind;
    pageUrl?: string;
    onMissingAttribute?: (details: {
        kind: EditorialRouteKind;
        pageId?: string;
        pageTypeId?: string;
        pageUrl?: string;
    }) => void;
};

export function logMissingEditorialContentGroup(
    logger: { warn: (message: string, meta?: Record<string, unknown>) => void },
    details: {
        kind: EditorialRouteKind;
        pageId?: string;
        pageTypeId?: string;
        pageUrl?: string;
    }
): void {
    logger.warn('Editorial route missing analyticsContentGroup Page Designer attribute', details);
}

export function logMissingCategoryPillarContentGroup(
    logger: { warn: (message: string, meta?: Record<string, unknown>) => void },
    details: {
        kind: CategoryBoundPillarPageKind;
        pageId?: string;
        pageTypeId?: string;
        pageUrl?: string;
        categoryId: string;
    }
): void {
    logger.warn('Category pillar page missing analyticsContentGroup Page Designer attribute', details);
}

export function resolveEditorialRouteContentGroup(
    args: ResolveEditorialRouteContentGroupArgs
): ContentGroupValue | undefined {
    if (args.kind === 'newsListing' || args.page?.typeId === 'newsListingPage') {
        return CONTENT_GROUP.NEWS_LIST;
    }

    if (args.page?.typeId === 'eatalyClubPage') {
        return CONTENT_GROUP.CLUB;
    }

    const fromPage = readPageDesignerContentGroup(args.page);
    if (fromPage) {
        return fromPage;
    }

    args.onMissingAttribute?.({
        kind: args.kind,
        pageId: args.page?.id,
        pageTypeId: args.page?.typeId,
        pageUrl: args.pageUrl,
    });

    return undefined;
}

export function resolvePdpContentGroup(
    product: Pick<SCAPIShopperProductsSchemas['Product'], 'c_productType'>
): ContentGroupValue {
    if (product.c_productType === 'course') {
        return CONTENT_GROUP.CLASS_DETAILS;
    }
    if (product.c_productType === 'event') {
        return CONTENT_GROUP.EVENT_DETAILS;
    }
    return CONTENT_GROUP.PRODUCT_DETAILS;
}

const PILLAR_HOME_CONTENT_GROUP: Record<NavigationPillar, ContentGroupValue> = {
    onlineShop: CONTENT_GROUP.HOME_ONLINE_SHOP,
    events: CONTENT_GROUP.HOME_EVENTS,
    courses: CONTENT_GROUP.HOME_CLASSES,
    restaurants: CONTENT_GROUP.HOME_RESTAURANTS,
    business: CONTENT_GROUP.HOME_BUSINESS,
};

/** Home and pillar landing pages; splash when no pillar is known. */
export function resolvePillarHomeContentGroup(pillar: NavigationPillar | null | undefined): ContentGroupValue {
    return pillar ? PILLAR_HOME_CONTENT_GROUP[pillar] : CONTENT_GROUP.HOME_SPLASH;
}

export type ResolveCategoryBoundPillarContentGroupArgs = {
    page: Pick<PageWithComponentData, 'data' | 'id' | 'typeId'>;
    categoryId: string;
    pillar: NavigationPillar | undefined;
    kind: CategoryBoundPillarPageKind;
    pageUrl?: string;
    onMissingAttribute?: (details: {
        kind: CategoryBoundPillarPageKind;
        pageId?: string;
        pageTypeId?: string;
        pageUrl?: string;
        categoryId: string;
    }) => void;
};

/** Prefers Page Designer `analyticsContentGroup`; falls back to the category's navigation pillar when unset. */
export function resolveCategoryBoundPillarContentGroup(
    args: ResolveCategoryBoundPillarContentGroupArgs
): ContentGroupValue {
    const fromPage = readPageDesignerContentGroup(args.page);
    if (fromPage) {
        return fromPage;
    }

    args.onMissingAttribute?.({
        kind: args.kind,
        pageId: args.page.id,
        pageTypeId: args.page.typeId,
        pageUrl: args.pageUrl,
        categoryId: args.categoryId,
    });

    return resolvePillarHomeContentGroup(args.pillar);
}

export function resolvePlpContentGroup({
    pillar,
    useEventListingLayout,
    kind,
}: {
    pillar: NavigationPillar | undefined;
    useEventListingLayout: boolean;
    kind: PlpRouteKind;
}): ContentGroupValue {
    if (kind === 'pillarPage' || kind === 'fullEditorialCategoryPage') {
        return resolvePillarHomeContentGroup(pillar);
    }

    if (useEventListingLayout) {
        if (pillar === 'courses') {
            return CONTENT_GROUP.CLASS_LIST;
        }
        if (pillar === 'events') {
            return CONTENT_GROUP.EVENT_LIST;
        }
    }

    return CONTENT_GROUP.PRODUCT_LIST;
}

/** Minimal pathname fallback when a route loader does not expose analytics metadata yet. */
export function resolveContentGroupFromPathname(pathname: string): ContentGroupValue | undefined {
    if (pathname === '/') {
        return CONTENT_GROUP.HOME_SPLASH;
    }
    if (pathname.startsWith('/p/') || pathname.startsWith('/product/')) {
        return CONTENT_GROUP.PRODUCT_DETAILS;
    }
    if (pathname.startsWith('/c/') || pathname.startsWith('/category/')) {
        return CONTENT_GROUP.PRODUCT_LIST;
    }
    if (pathname.startsWith('/search')) {
        return CONTENT_GROUP.SEARCH;
    }
    if (pathname.startsWith('/cart')) {
        return CONTENT_GROUP.CART;
    }
    if (pathname.startsWith('/checkout')) {
        return CONTENT_GROUP.CHECKOUT;
    }
    if (pathname.startsWith('/order-confirmation')) {
        return CONTENT_GROUP.ORDER_CONFIRMATION;
    }
    if (pathname.startsWith('/login') || pathname.startsWith('/signup')) {
        return CONTENT_GROUP.LOGIN_SIGNUP;
    }
    if (pathname.startsWith('/account') || pathname.startsWith('/wishlist')) {
        return CONTENT_GROUP.ACCOUNT;
    }

    return undefined;
}
